import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  parentFacilityDisplayName,
  parentTypeAbbrev,
  type ParentLinkRequestBannerData,
} from "./parentLinkRequestUtils";
import RequestDialogFacilityCard from "./RequestDialogFacilityCard";
import {
  requestDialogBodySx,
  requestDialogFooterSx,
  requestDialogHeaderSx,
  requestDialogPaperSx,
  requestDialogShellSx,
} from "./requestDialogLayout";
import { FN } from "./facilityNetworkTheme";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import chevronWhite from "@assets/facilityNetwork/req-chevron-white.svg";

type Bullet = { text: string; tone: "teal" | "red" };

const ACCEPTANCE_POINTS: Bullet[] = [
  {
    text: "The parent facility will be able to view your compliance status and open items",
    tone: "teal",
  },
  {
    text: "Your facility continues to operate independently — no settings or data will change",
    tone: "teal",
  },
  {
    text: "You can unlink from the parent at any time from My Network",
    tone: "teal",
  },
  {
    text: "The parent cannot edit your compliance data, assignees, or notes",
    tone: "red",
  },
  {
    text: "You will no longer be able to add child facilities or send link requests to other facilities",
    tone: "red",
  },
];

type Props = {
  open: boolean;
  request: ParentLinkRequestBannerData | null;
  submitting?: boolean;
  onClose: () => void;
  onDecline: (reason?: string) => void;
  onAccept: () => void;
};

/** Figma 9128:96387 Default + 9128:96389 decline — Review link request */
export default function ParentLinkRequestDialog({
  open,
  request,
  submitting = false,
  onClose,
  onDecline,
  onAccept,
}: Props) {
  const [isDeclining, setIsDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  useEffect(() => {
    if (!open) {
      setIsDeclining(false);
      setDeclineReason("");
    }
  }, [open, request?.requestId]);

  if (!request) return null;

  const typeAbbrev = parentTypeAbbrev(request);
  const displayName = parentFacilityDisplayName(request);
  const metaItems = [
    { label: "CCN", value: request.ccn?.trim() || "NA" },
    { label: "NPI", value: request.npi?.trim() || "NA" },
    { label: "Type", value: typeAbbrev },
    ...(request.parentState ? [{ label: "State", value: request.parentState }] : []),
  ];
  const acceptLabel = request.coordinationMode
    ? "Accept and request unlink"
    : "Accept and Link";

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth={false}
      PaperProps={{ sx: requestDialogPaperSx }}
    >
      <Box sx={requestDialogShellSx}>
        <Box sx={requestDialogHeaderSx}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              color: "#242424",
              lineHeight: "20px",
            }}
          >
            Link request
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            sx={{ borderRadius: "4px", p: "2px" }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <Box sx={requestDialogBodySx}>
          <RequestDialogFacilityCard
            sectionLabel="Requesting parent facility"
            facilityName={displayName}
            metaItems={metaItems}
          />

          {request.coordinationMode && request.currentParentFacilityName ? (
            <Box
              sx={{
                bgcolor: "#fff4e5",
                border: "1px solid #fabc89",
                borderRadius: "4px",
                p: "8px 12px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 14,
                  color: "#8b4409",
                  lineHeight: "20px",
                }}
              >
                {`Accepting sends an unlink request to your current parent ${request.currentParentFacilityName}. The link with ${request.parentFacilityName} only establishes if ${request.currentParentFacilityName} approves the unlink. If ${request.currentParentFacilityName} disapproves, this request is cancelled and you remain linked to ${request.currentParentFacilityName}.`}
              </Typography>
            </Box>
          ) : null}

          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", pt: isDeclining ? 0 : 1 }}>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: "#242424",
                lineHeight: "20px",
                mb: 0.25,
              }}
            >
              What accepting means
            </Typography>
            {ACCEPTANCE_POINTS.map((point) => (
              <Box
                key={point.text}
                sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%" }}
              >
                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "24px",
                    bgcolor: point.tone === "red" ? "#d13438" : FN.primary,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 12,
                    color: FN.figmaAccent,
                    letterSpacing: "0.03px",
                    lineHeight: "16px",
                    flex: 1,
                  }}
                >
                  {point.text}
                </Typography>
              </Box>
            ))}
          </Box>

          {isDeclining ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", pt: 1 }}>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#242424",
                  lineHeight: "20px",
                }}
              >
                Reason for declining (optional)
              </Typography>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 12,
                  color: FN.figmaAccent,
                  letterSpacing: "0.03px",
                  lineHeight: "16px",
                  mb: 0.5,
                }}
              >
                {`This note will be shared with ${request.parentFacilityName}.`}
              </Typography>
              <TextField
                multiline
                minRows={4}
                fullWidth
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="e.g. We do not recognize this organization as our parent facility..."
                disabled={submitting}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#fafafa",
                    borderRadius: "5px",
                    fontFamily: FN.fontFamily,
                    fontSize: 12,
                    color: FN.figmaAccent,
                    "& fieldset": { borderColor: "#e5e5e5" },
                  },
                }}
              />
            </Box>
          ) : null}
        </Box>

        <Box sx={requestDialogFooterSx}>
          <Button
            variant="outlined"
            disabled={submitting}
            onClick={() => {
              if (isDeclining) {
                setIsDeclining(false);
                setDeclineReason("");
                return;
              }
              onClose();
            }}
            sx={{
              textTransform: "none",
              height: 32,
              minHeight: 32,
              px: 1.5,
              borderRadius: "4px",
              borderColor: "#d4d4d4",
              color: FN.text,
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              bgcolor: "#fff",
            }}
          >
            Cancel
          </Button>
          {!isDeclining ? (
            <Button
              variant="outlined"
              disabled={submitting}
              onClick={() => setIsDeclining(true)}
              sx={{
                textTransform: "none",
                height: 32,
                minHeight: 32,
                px: 1.5,
                borderRadius: "4px",
                borderColor: FN.figmaErrorStroke,
                color: FN.figmaError,
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                bgcolor: "#fff",
              }}
            >
              Decline
            </Button>
          ) : (
            <Button
              variant="outlined"
              disabled={submitting}
              onClick={() => onDecline(declineReason.trim() || undefined)}
              sx={{
                textTransform: "none",
                height: 32,
                minHeight: 32,
                px: 1.5,
                borderRadius: "4px",
                borderColor: FN.figmaErrorStroke,
                color: FN.figmaError,
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                bgcolor: "#fff",
              }}
            >
              Confirm decline
            </Button>
          )}
          {!isDeclining ? (
            <Button
              variant="contained"
              disabled={submitting}
              onClick={onAccept}
              endIcon={
                submitting ? (
                  <CircularProgress size={12} sx={{ color: "#fff" }} />
                ) : (
                  <FacilityNetworkFigmaIcon src={chevronWhite} size={12} />
                )
              }
              sx={{
                textTransform: "none",
                height: 32,
                minHeight: 32,
                px: 2,
                borderRadius: "4px",
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: "#fff",
                background: "linear-gradient(90deg, #0a829d 0%, #58c2d8 100%)",
                boxShadow: "0px 2px 4px rgba(0,0,0,0.25)",
                "&:hover": {
                  background: "linear-gradient(90deg, #065b70 0%, #0a829d 100%)",
                },
                "& .MuiButton-endIcon": { ml: 1 },
              }}
            >
              {acceptLabel}
            </Button>
          ) : null}
        </Box>
      </Box>
    </Dialog>
  );
}
